import createClient, { type Client, type Middleware } from 'openapi-fetch'

import {
  ApiConflictError,
  ApiContractError,
  ApiError,
  ApiNotFoundError,
  ApiRateLimitError,
  ApiValidationError,
  type ErrorBody,
  isConflictReason,
  MissingCsrfCookieError,
} from './errors'
import type { paths } from './schema'
import { fetchWithProgress, type UploadProgress } from './upload'

export type ApiClient = Client<paths>

export interface ApiClientOptions {
  /** Origin of tendly-api without a trailing slash. */
  readonly baseUrl: string
  /** Called once for every 404, before the ApiNotFoundError is thrown. */
  readonly onNotFound: () => void
  /** Injection point for tests. Defaults to the global fetch. */
  readonly fetch?: (request: Request) => Promise<Response>
  /** Injection point for tests. Defaults to reading document.cookie. */
  readonly readCookie?: (name: string) => string | null
}

const XSRF_COOKIE = 'XSRF-TOKEN'
const XSRF_HEADER = 'X-XSRF-TOKEN'
const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS'])

function readDocumentCookie(name: string): string | null {
  const prefix = `${name}=`
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return part.substring(prefix.length)
    }
  }
  return null
}

/**
 * Sanctum SPA session (MRS 7.2): the session cookie travels with every call
 * (`credentials: include`) and every state-changing call carries the CSRF
 * token. Laravel URL-encodes the cookie value, so it is decoded first; sent
 * raw, every POST would answer 419.
 */
function csrfMiddleware(readCookie: (name: string) => string | null): Middleware {
  return {
    onRequest({ request }) {
      if (SAFE_METHODS.has(request.method)) {
        return undefined
      }
      const token = readCookie(XSRF_COOKIE)
      if (token === null || token === '') {
        throw new MissingCsrfCookieError(request.method, request.url)
      }
      request.headers.set(XSRF_HEADER, decodeURIComponent(token))
      return undefined
    },
  }
}

async function readErrorBody(response: Response): Promise<ErrorBody | null> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    return null
  }
  const parsed: unknown = await response.json()
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    typeof (parsed as { message?: unknown }).message === 'string'
  ) {
    return parsed as ErrorBody
  }
  return null
}

function hasFieldErrors(
  body: ErrorBody | null,
): body is ErrorBody & { errors: Record<string, string[]> } {
  return body?.errors !== undefined && Object.keys(body.errors).length > 0
}

function parseRetryAfter(response: Response): number | null {
  const header = response.headers.get('retry-after')
  if (header === null) {
    return null
  }
  const seconds = Number.parseInt(header, 10)
  return Number.isNaN(seconds) || seconds < 0 ? null : seconds
}

/**
 * The MRS 9 error contract, applied once for every call:
 * 422 becomes field errors, 409 a reason code, 404 the not-found page.
 * Every other non-2xx status is an ApiError. A successful call returns
 * `{ data }` as usual; a failed one throws, so callers never see `error`.
 */
function errorContractMiddleware(onNotFound: () => void): Middleware {
  return {
    async onResponse({ response }) {
      if (response.ok) {
        return undefined
      }
      const body = await readErrorBody(response)

      switch (response.status) {
        case 422:
          if (!hasFieldErrors(body)) {
            throw new ApiContractError(422, body, 'a 422 must carry field errors')
          }
          throw new ApiValidationError(body)
        case 409:
          if (body === null || !isConflictReason(body.reason)) {
            throw new ApiContractError(409, body, `a 409 must carry a known reason`)
          }
          throw new ApiConflictError({ ...body, reason: body.reason })
        case 404:
          onNotFound()
          throw new ApiNotFoundError(body)
        case 429:
          throw new ApiRateLimitError(body, parseRetryAfter(response))
        default:
          throw new ApiError(response.status, body)
      }
    },
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(options.fetch ? { fetch: options.fetch } : {}),
  })
  client.use(
    csrfMiddleware(options.readCookie ?? readDocumentCookie),
    errorContractMiddleware(options.onNotFound),
  )
  return client
}

/** The body of a call that the error contract let through. */
export function body<T>(result: { data?: T }): T {
  if (result.data === undefined) {
    throw new Error('The API answered a success with no body where the contract promises one.')
  }
  return result.data
}

let configured: ApiClient | null = null
/** The origin of tendly-api that the client talks to. */
let origin: string | null = null
/** The `fetch` that a test gave. A test has no network, so its uploads go through it too. */
let scripted: ((request: Request) => Promise<Response>) | null = null

/** Builds the application client once, at start-up (main.ts). */
export function configureApiClient(options: ApiClientOptions): ApiClient {
  if (configured !== null) {
    throw new Error('configureApiClient was called twice.')
  }
  configured = createApiClient(options)
  origin = options.baseUrl
  scripted = options.fetch ?? null
  return configured
}

/** The application client. Throws when main.ts has not configured it yet. */
export function useApi(): ApiClient {
  if (configured === null) {
    throw new Error('The API client is not configured. Call configureApiClient in main.ts first.')
  }
  return configured
}

/** The origin of tendly-api, for a text that shows an address of the API. */
export function apiOrigin(): string {
  if (origin === null) {
    throw new Error('The API client is not configured. Call configureApiClient in main.ts first.')
  }
  return origin
}

/** Only for tests: forgets the configured client. */
export function _resetApiClient(): void {
  configured = null
  origin = null
  scripted = null
}

/**
 * The `fetch` of one call that carries files: it says how much is sent. Give
 * it to the call as its `fetch` option.
 */
function uploadFetch(onProgress: UploadProgress): (request: Request) => Promise<Response> {
  return scripted ?? fetchWithProgress(onProgress)
}

/**
 * The options of a call that can carry files. With files the call is a form
 * with parts, sent so that the page sees how far it is. With none it is the
 * plain call.
 */
export function withUpload(files: readonly File[], onProgress: UploadProgress) {
  return files.length > 0
    ? { bodySerializer: withFiles(files), fetch: uploadFetch(onProgress) }
    : {}
}

/**
 * The body of a call that carries files (`multipart/form-data` of the
 * contract): each field of `fields` as text, each number of a list field
 * under `name[]`, and each file under `files[]`.
 */
function withFiles(files: readonly File[]): (fields: object) => FormData {
  return (fields) => {
    const form = new FormData()
    for (const [name, value] of Object.entries(fields)) appendField(form, name, value)
    for (const file of files) form.append('files[]', file, file.name)
    return form
  }
}

/** One field of a call with files. A list of ids, such as `video_ids`, is one `name[]` part for each id. */
function appendField(form: FormData, name: string, value: unknown): void {
  if (value === null || value === undefined) return
  if (Array.isArray(value)) {
    for (const item of value as unknown[]) {
      if (typeof item !== 'number') {
        throw new Error(`The list ${name} of a call with files must hold numbers.`)
      }
      form.append(`${name}[]`, String(item))
    }
    return
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    throw new Error(`The field ${name} of a call with files must be text or a number.`)
  }
  form.append(name, String(value))
}
